import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { client } from "@/hera-api/client/client.gen";
import type {
  AdditionalQuestionAnswer,
  LicenseStatus,
} from "@/hera-api/client/types.gen";
import { formatDateNoTimezone, getLicenseStatusLabel } from "@/lib/utils";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type TransferableLicenseOption = {
  license_id: string;
  display_id: string;
  status: string;
  license_type_id: string;
  license_type_name: string | null;
  registrant_name: string | null;
  registrant_email: string | null;
  property_address1: string | null;
  property_apn: string | null;
  omt_name: string | null;
  ams_name: string | null;
  effective_date: string;
  case_number: string | null;
  answers_to_additional_questions: AdditionalQuestionAnswer[];
};

type Mode = "new" | "transfer";

interface TransferExistingLicensePromptProps {
  jurisdictionId: string | null;
  apn: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  latitude: number | null;
  longitude: number | null;
  selectedTransferLicenseId: string | null;
  stepNumber: number;
  onPrevious: () => void;
  onLookupComplete: (hasMatches: boolean) => void;
  onSelectTransferLicense: (license: TransferableLicenseOption | null) => void;
  onContinue: (mode: Mode, license: TransferableLicenseOption | null) => void;
}

function Detail({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{value || "Not provided"}</dd>
    </div>
  );
}

function LicenseDetails({ license }: { license: TransferableLicenseOption }) {
  return (
    <>
      <div className="font-medium">
        {license.display_id}
        {license.license_type_name ? ` · ${license.license_type_name}` : null}
      </div>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Detail label="Registrant" value={license.registrant_name} />
        <Detail label="Registrant email" value={license.registrant_email} />
        <Detail label="OMT" value={license.omt_name} />
        <Detail label="AMS" value={license.ams_name} />
        {license.case_number ? (
          <Detail label="Case number" value={license.case_number} />
        ) : null}
        <Detail
          label="Effective date"
          value={formatDateNoTimezone(license.effective_date)}
        />
        <Detail
          label="Status"
          value={getLicenseStatusLabel(license.status as LicenseStatus)}
        />
        <Detail label="Property" value={license.property_address1} />
        <Detail label="APN" value={license.property_apn} />
      </dl>
    </>
  );
}

/**
 * Dedicated registration-as-transfer step. It looks up likely registrations
 * for the verified property before the registration-question step.
 */
export function TransferExistingLicensePrompt({
  jurisdictionId,
  apn,
  address1,
  address2,
  city,
  state,
  zipcode,
  latitude,
  longitude,
  selectedTransferLicenseId,
  stepNumber,
  onPrevious,
  onLookupComplete,
  onSelectTransferLicense,
  onContinue,
}: TransferExistingLicensePromptProps) {
  const [licenses, setLicenses] = useState<TransferableLicenseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onLookupCompleteRef = useRef(onLookupComplete);

  useEffect(() => {
    onLookupCompleteRef.current = onLookupComplete;
  }, [onLookupComplete]);

  useEffect(() => {
    if (!jurisdictionId || (!apn?.trim() && !address1?.trim())) {
      setLicenses([]);
      onLookupCompleteRef.current(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await client.get({
          url: "/registrant/licenses/active-by-property",
          query: {
            jurisdiction_id: jurisdictionId,
            apn,
            address1,
            address2,
            city,
            state,
            zipcode,
            latitude,
            longitude,
          },
          parseAs: "json",
          security: [{ scheme: "bearer", type: "http" }],
        });
        if (cancelled) return;
        if (response.error) {
          setError("Could not look up existing registrations");
          setLicenses([]);
          return;
        }
        const data = response.data as {
          licenses?: TransferableLicenseOption[];
        };
        const matches = (data.licenses ?? []).map((license) => ({
          ...license,
          case_number: license.case_number ?? null,
        }));
        setLicenses(matches);
        onLookupCompleteRef.current(matches.length > 0);
      } catch {
        if (!cancelled) {
          setError("Could not look up existing registrations");
          setLicenses([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    jurisdictionId,
    apn,
    address1,
    address2,
    city,
    state,
    zipcode,
    latitude,
    longitude,
  ]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Step {stepNumber}: Existing Registration Check</CardTitle>
          <CardDescription>
            Checking for existing registrations at this property…
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Step {stepNumber}: Existing Registration Check</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardFooter className="justify-between">
          <Button type="button" variant="outline" onClick={onPrevious}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
          <Button type="button" onClick={() => onContinue("new", null)}>
            Continue
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>
    );
  }

  if (licenses.length === 0) {
    return null;
  }

  const soleLicense = licenses.length === 1 ? licenses[0] : null;
  const selectedLicense =
    soleLicense ??
    licenses.find(
      (license) => license.license_id === selectedTransferLicenseId,
    ) ??
    null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step {stepNumber}: Existing Registration Check</CardTitle>
        <CardDescription>
          {soleLicense
            ? "There appears to be an existing registration at this property. Review the details below, then transfer it or continue with a new registration."
            : "There appears to be existing registration found at this property. Review the details below, then transfer an existing registration or continue with a new registration."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {soleLicense ? (
          <div className="rounded-md border p-4">
            <LicenseDetails license={soleLicense} />
          </div>
        ) : (
          licenses.map((license) => (
            <label
              key={license.license_id}
              className="block cursor-pointer rounded-md border p-4 has-[:checked]:border-primary has-[:checked]:ring-1 has-[:checked]:ring-primary"
            >
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="transfer-license"
                  className="mt-1"
                  checked={selectedTransferLicenseId === license.license_id}
                  onChange={() => onSelectTransferLicense(license)}
                  aria-label={`Select registration ${license.display_id}`}
                />
                <div className="min-w-0 flex-1">
                  <LicenseDetails license={license} />
                </div>
              </div>
            </label>
          ))
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-2">
        <Button type="button" variant="outline" onClick={onPrevious}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onContinue("new", null)}
          >
            Continue with new registration
          </Button>
          <Button
            type="button"
            disabled={!selectedLicense}
            onClick={() => onContinue("transfer", selectedLicense)}
          >
            {soleLicense ? "Transfer" : "Transfer selected"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
